import { nextPollInterval, shouldContinuePolling } from '../utils/poll_utils';
import { nextUpdateDelayMs, comparableScoringStatus } from '../utils/scoring_status_utils';

export const ActionTypes = {
  FETCH_SCORING_STATUS_SUCCESS: 'leaderboardV2/FETCH_SCORING_STATUS_SUCCESS',
};

// Poll shortly after the announced scoring run instead of exactly on it, so
// the job has landed by the time we ask.
const NEXT_UPDATE_BUFFER_MS = 2000;

// Ceiling on how far out an announced run may park the poller when the
// customer has no pollMaxInterval: a stale or skewed job available_time would
// otherwise freeze the status bar for hours with no self-correction, since
// the next poll IS the sleeping timer.
const MAX_ANNOUNCED_DELAY_MS = 5 * 60 * 1000;

// Single poller — one status bar per page. The active chain is identified by
// object identity: a callback that resumes after a stop/restart sees a
// different object and exits instead of scheduling a duplicate chain.
let activePoll = null;

const fetchScoringStatus = () => async (dispatch, getState) => {
  const { scoringStatusUrl, roundId } = getState().leaderboardV2;

  const res = await fetch(scoringStatusUrl.replace(':round_id', roundId));
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const data = await res.json();
  const messages = Array.isArray(data?.messages) ? data.messages : [];

  dispatch({ type: ActionTypes.FETCH_SCORING_STATUS_SUCCESS, payload: { messages } });

  return messages;
};

// When the API announces the next scoring run, poll just after it lands;
// otherwise back off exactly like the event polling does.
const nextStatusInterval = (interval, messages, changed, leaderboardV2State) => {
  const fallback = nextPollInterval(interval, changed, leaderboardV2State);
  const target = nextUpdateDelayMs(messages);
  if (target == null) return fallback;

  const { pollMinInterval, pollMaxInterval } = leaderboardV2State;
  const buffered = target + NEXT_UPDATE_BUFFER_MS;

  return Math.max(pollMinInterval, Math.min(buffered, pollMaxInterval || MAX_ANNOUNCED_DELAY_MS));
};

const schedulePoll = (poll, interval, dispatch, getState) => {
  poll.timerId = setTimeout(async () => {
    if (activePoll !== poll) return;

    const previous = comparableScoringStatus(getState().leaderboardV2.scoringStatus);

    let messages = null;
    try {
      messages = await dispatch(fetchScoringStatus());
    } catch {
      // Keep polling — a transient error leaves the last status on screen.
    }

    // Re-check after the await: polling may have been restarted meanwhile.
    if (activePoll !== poll) return;

    // Legacy stop rule (shouldContinuePolling): a not-ongoing round gets this one
    // poll, then the chain ends - unless the customer's polling optimization is
    // off, in which case polling never stops.
    if (!shouldContinuePolling(getState().leaderboardV2)) {
      activePoll = null;
      return;
    }

    const changed = messages != null && comparableScoringStatus(messages) !== previous;
    const next = nextStatusInterval(interval, messages, changed, getState().leaderboardV2);
    schedulePoll(poll, next, dispatch, getState);
  }, interval);
};

export const startScoringStatusPolling = () => async (dispatch, getState) => {
  stopScoringStatusPolling();

  const poll = { timerId: null };
  activePoll = poll;

  let messages = null;
  try {
    messages = await dispatch(fetchScoringStatus());
  } catch {
    // First fetch failed — the server-rendered status stays; polling continues.
  }

  if (activePoll !== poll) return;

  const { leaderboardV2 } = getState();

  // Legacy stop rule (shouldContinuePolling): a not-ongoing round is fetched
  // once and not polled - unless the customer's polling optimization is off.
  if (!shouldContinuePolling(leaderboardV2)) {
    activePoll = null;
    return;
  }

  // changed: true — the first poll starts from the minimum interval, unless
  // the response announces the next scoring run.
  const interval = nextStatusInterval(leaderboardV2.pollMinInterval, messages, true, leaderboardV2);
  schedulePoll(poll, interval, dispatch, getState);
};

export const stopScoringStatusPolling = () => {
  if (!activePoll) return;

  clearTimeout(activePoll.timerId);
  activePoll = null;
};
